import { useLoader } from '@/context/Loader';
import { useMessage } from '@/context/Message';
import { useOidc } from '@/context/Oidc';
import { useLog } from '@/features/shared/Hooks/useLog';
import { useMobileSnackBarPosition } from '@/hooks/useMobileSnackBarPosition';
import { usePostMessageListener } from '@/hooks/usePostMessageListener';
import {
  POWERAPPS_MINUTES_EXPIRATION,
  POWERAPPS_SIGN_IN_URI,
  POWERAPPS_SIGN_OUT_URI,
} from '@/utils/constants';
import FramePopupUtils from '@/utils/FramePopupUtils';
import { getGenetecPostMessage } from '@/utils/helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useStorageState } from 'react-storage-hooks';
import {
  PowerAppsConnectProps,
  POWERAPPS_EXPIRATION_TIME_KEY,
  POWERAPPS_INITIATED_KEY,
} from '../types';
import { usePowerAppsCookieKeys } from '../usePowerAppsCookieKeys';

const postMessageConnection = getGenetecPostMessage({ source: 'genetec', type: 'connection' });

export const usePowerAppsLoader = () => {
  const { forceLoad } = useLoader();
  const { userData } = useOidc();
  const { error } = useMessage();
  const { messagePosition } = useMobileSnackBarPosition();

  const iframeRef = useRef<HTMLIFrameElement>();
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupFailed, setPopupFailed] = useState(false);
  const [useGlobalLoader, setUseGlobalLoader] = useState(false);
  const loadMessageTriggered = useRef<boolean>(false);
  const onLoadMessage = useRef<() => void>(() => {});
  const Log = useLog({ context: 'usePowerAppsLoader' });
  const waitingForConnection = useRef<((value: void | PromiseLike<void>) => void)[]>([]);
  const expirationTimeout = useRef<NodeJS.Timeout>();
  const [isLoginOut, setIsLoginOut] = useState(false);
  const isLoading = useMemo(
    () => isLoginOut || iframeLoading || popupLoading,
    [isLoginOut, iframeLoading, popupLoading],
  );
  const hasFailed = useMemo(() => iframeFailed && popupFailed, [iframeFailed, popupFailed]);

  const [PA_INITIATED_KEY, PA_EXPIRATION_TIME_KEY] = usePowerAppsCookieKeys([
    POWERAPPS_INITIATED_KEY,
    POWERAPPS_EXPIRATION_TIME_KEY,
  ]);

  const [paInitiated, setPaInitiated] = useStorageState<boolean>(localStorage, PA_INITIATED_KEY);

  const [{ [PA_EXPIRATION_TIME_KEY]: powerAppsExpirationTime }, setCookie, removeCookie] =
    useCookies<any>([PA_EXPIRATION_TIME_KEY]);

  const isLoaded = useMemo(
    () => Boolean(paInitiated && powerAppsExpirationTime > 0),
    [paInitiated, powerAppsExpirationTime],
  );

  const getIsLoaded = useCallback(() => {
    const paInitiatedValue = localStorage.getItem(PA_INITIATED_KEY);
    try {
      return JSON.parse(paInitiatedValue || '');
    } catch (e) {
      return false;
    }
  }, [PA_INITIATED_KEY]);

  useEffect(() => {
    Log('PowersApps is connected', isLoaded);
  }, [isLoaded, Log]);

  const clearOldInitiated = useCallback(() => {
    const paInitiatedKeys = Object.keys(localStorage).filter(
      (key) => key.startsWith(POWERAPPS_INITIATED_KEY) && key !== PA_INITIATED_KEY,
    );

    paInitiatedKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  }, [PA_INITIATED_KEY]);

  const setExpirationTime = useCallback(() => {
    const expires = new Date(Date.now() + POWERAPPS_MINUTES_EXPIRATION * 60000);

    setCookie(PA_EXPIRATION_TIME_KEY, expires.getTime(), {
      expires,
      path: '/',
    });

    setIsSessionExpired(false);
  }, [PA_EXPIRATION_TIME_KEY, setCookie]);

  const setIsLoaded = useCallback(
    (value: boolean) => {
      if (isLoaded && value) return;

      if (value) {
        clearOldInitiated();
        setPaInitiated(value);
        setIframeFailed(false);
        setExpirationTime();
      } else {
        removeCookie(PA_EXPIRATION_TIME_KEY);
      }
    },
    [
      clearOldInitiated,
      isLoaded,
      setPaInitiated,
      setExpirationTime,
      removeCookie,
      PA_EXPIRATION_TIME_KEY,
    ],
  );

  useEffect(() => {
    if (powerAppsExpirationTime) {
      if (expirationTimeout.current) {
        clearTimeout(expirationTimeout.current);
      }

      expirationTimeout.current = setTimeout(
        () => {
          Log('Session Timeout');
          setIsSessionExpired(true);
        },
        Number(powerAppsExpirationTime) - Date.now(),
      );
    }

    return () => {
      if (expirationTimeout.current) clearTimeout(expirationTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerAppsExpirationTime]);

  useEffect(() => {
    if (isLoading) {
      forceLoad(true);
      return;
    }

    if (!isLoading) {
      forceLoad(false);
    }
  }, [forceLoad, isLoading]);

  const resolveWaitingForConnection = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));

    waitingForConnection.current.forEach((resolve) => {
      resolve();
    });

    waitingForConnection.current = [];
  }, []);

  const handleLoadMessage = useCallback(
    (props: any) => {
      const data = postMessageConnection(props);
      if (!data) return;

      if (!/loaded/.test(data?.event)) return;
      if (loadMessageTriggered.current) return;

      window.removeEventListener('message', handleLoadMessage);

      if (isLoaded) {
        Log('"load message" triggered');

        onLoadMessage.current();
        return;
      }

      Log('"load message" triggered before loaded');
      loadMessageTriggered.current = true;
      onLoadMessage.current();
      setIsLoaded(true);
    },
    [Log, isLoaded, setIsLoaded],
  );

  const monitorLoadMessageTriggered = useMemo(
    () => (ms: number, callback: () => void) => {
      return setInterval(() => {
        if (loadMessageTriggered.current && callback) {
          callback();
        }
      }, ms);
    },
    [],
  );

  const retryWithPopup = useCallback(() => {
    setPopupFailed(false);
    setPopupLoading(true);
    Log('retrying with popup');

    let failedTimer: NodeJS.Timeout;
    let loadedTimer: NodeJS.Timeout;

    // Open Popup window
    const popUpWindow = FramePopupUtils.openSizedPopup({
      urlNavigate: POWERAPPS_SIGN_IN_URI,
      popupName: 'usePowerAppsLoader',
      height: 112,
      width: 426,
    });

    const handleOnLoadMessage = async () => {
      // Iframe Loaded
      setIsLoaded(true);
      setPopupLoading(false);
      Log('popup loaded');

      await resolveWaitingForConnection();

      if (failedTimer) clearInterval(failedTimer);
      if (loadedTimer) clearInterval(loadedTimer);

      if (popUpWindow) {
        popUpWindow.close();
      }
    };

    onLoadMessage.current = handleOnLoadMessage;

    if (popUpWindow && !popUpWindow.closed) {
      loadedTimer = monitorLoadMessageTriggered(500, handleOnLoadMessage);
      failedTimer = setInterval(() => {
        if (popUpWindow.closed) {
          if (failedTimer) clearInterval(failedTimer);
          if (loadedTimer) clearInterval(loadedTimer);

          // Popup Failed
          error({
            title: 'Popup closed unexpectedly',
            message: 'Please click on "connect" to establish connection with CRM.',
            duration: null,
            actionLabel: 'CONNECT',
            onAction: retryWithPopup,
            position: messagePosition,
          });
          Log('popup closed');
        }
      }, 500);

      return;
    }

    // Popup Failed
    error({
      title: 'Popup blocked',
      message: 'Please click on "connect" to establish connection with CRM.',
      duration: null,
      actionLabel: 'CONNECT',
      onAction: retryWithPopup,
      position: messagePosition,
    });
    Log('popup blocked');
  }, [
    Log,
    monitorLoadMessageTriggered,
    setIsLoaded,
    error,
    resolveWaitingForConnection,
    messagePosition,
  ]);

  const handleFrameLoad = useCallback(() => {
    // eslint-disable-next-line prefer-const
    let timeoutTimer: NodeJS.Timeout;

    Log('on iframe load');

    const handleOnLoadMessage = async () => {
      iframeRef.current?.remove();

      // Iframe Loaded
      setIsLoaded(true);
      setIframeLoading(false);
      Log('iframe loaded');

      await resolveWaitingForConnection();

      clearTimeout(timeoutTimer);
      // clearTimeout(failureTimer);
    };

    Log('load message triggered', loadMessageTriggered.current);
    if (loadMessageTriggered.current) {
      handleOnLoadMessage();
      return;
    }

    onLoadMessage.current = handleOnLoadMessage;

    // Timer for in case the iframe does not emit load post message
    const timeoutLimit = 1000 * 60 * 2; // 2 min
    timeoutTimer = setTimeout(() => {
      try {
        Log('Executing iframe timeout callback');
        const iframeContentWindow = iframeRef?.current?.contentWindow;
        if (iframeContentWindow) {
          onLoadMessage.current();
          Log('load message timeout has been exceeded - forcing to load');
        }
      } catch (e) {
        console.error(e);
      }
    }, timeoutLimit);

    // failureTimer = setTimeout(() => {
    //   iframeRef.current?.remove();

    //   // Iframe Failed
    //   setIframeFailed(true);
    //   setIframeLoading(false);
    //   Log('iframe failed');

    //   // Retry with popup
    //   retryWithPopup();
    // }, 1000);
  }, [Log, setIsLoaded, resolveWaitingForConnection]);

  // Initial Load with iframe
  const load = useCallback(
    async (connectProps?: PowerAppsConnectProps) => {
      Log('Connecting with PowerApps');

      const { force = false, globalLoader = true } = connectProps || {};
      setUseGlobalLoader(globalLoader);

      Log('isLoading', isLoading);

      if (isLoading) {
        return new Promise<void>((resolve) => {
          waitingForConnection.current.push(resolve);
        });
      }

      Log('isLoaded', isLoaded);
      Log('isSessionExpired', isSessionExpired);
      Log('oidc userData', userData);
      Log('force connection', force);
      Log('globalLoader connection', globalLoader);

      if (isLoaded && !isSessionExpired && !force) {
        Log('Powerapps already connected');
        return Promise.resolve();
      }

      loadMessageTriggered.current = false;
      iframeRef.current = document.createElement('iframe');
      iframeRef.current.src = POWERAPPS_SIGN_IN_URI;
      iframeRef.current.onload = handleFrameLoad;
      iframeRef.current.style.visibility = 'hidden';
      iframeRef.current.style.position = 'absolute';
      iframeRef.current.style.display = 'none';
      document.body.appendChild(iframeRef.current);

      Log('loading iframe');
      setIframeLoading(true);
      setIframeFailed(false);

      return new Promise<void>((resolve) => {
        waitingForConnection.current.push(resolve);
      });
    },
    [Log, handleFrameLoad, isLoading, isLoaded, isSessionExpired, userData, setUseGlobalLoader],
  );

  // Unload with iframe
  const unload = useCallback(() => {
    if (!getIsLoaded()) return Promise.resolve();

    setIsLoginOut(true);

    return new Promise<void>((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.src = POWERAPPS_SIGN_OUT_URI;
      iframe.onload = () => {
        // Mitigates race condition where logoff didn't kill the session in time
        setTimeout(() => {
          setIsLoaded(false);
          iframe.remove();
          Log('unloaded');
          resolve();
        }, 1000);
      };
      iframe.style.visibility = 'hidden';
      iframe.style.position = 'absolute';
      iframe.style.display = 'none';

      document.body.appendChild(iframe);

      Log('unloading');
    });
  }, [Log, getIsLoaded, setIsLoaded]);

  const refreshSession = useCallback(async () => {
    if (!isSessionExpired) return;

    setExpirationTime();
  }, [setExpirationTime, isSessionExpired]);

  usePostMessageListener(handleLoadMessage);

  return {
    load,
    unload,
    isLoading,
    isLoaded,
    hasFailed,
    iframeFailed,
    isSessionExpired,
    reload: retryWithPopup,
    refreshSession,
    useGlobalLoader,
  };
};
